import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconType } from 'react-icons';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

interface TeacherSidebarProps {
  className?: string;
}

const ChevronDownIcon = FaChevronDown as IconType;
const ChevronRightIcon = FaChevronRight as IconType;

const IconComponent = ({ icon: Icon, className }: { icon: IconType; className?: string }) => {
  const IconComp = Icon as any;
  return <IconComp className={className} />;
};

const TeacherSidebar: React.FC<TeacherSidebarProps> = ({ className }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  const [isMasterMenuOpen, setIsMasterMenuOpen] = useState(true);
  const [isExamMenuOpen, setIsExamMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  const closeAllExcept = (menu: string) => {
    if (menu !== 'master') setIsMasterMenuOpen(false);
    if (menu !== 'exam') setIsExamMenuOpen(false);
    if (menu !== 'account') setIsAccountMenuOpen(false);
  };

  const toggleMaster = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('master');
    setIsMasterMenuOpen(!isMasterMenuOpen);
  };
  const toggleExam = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('exam');
    setIsExamMenuOpen(!isExamMenuOpen);
  };
  const toggleAccount = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('account');
    setIsAccountMenuOpen(!isAccountMenuOpen);
  };

  const isActiveLink = (path: string): boolean => {
    return currentPath === `/teacher${path}` || currentPath.startsWith(`/teacher${path}/`);
  };

  const menuItemClassName = (active: boolean): string => `
    flex items-center min-h-[40px] pl-10 pr-4 py-1.5 rounded-md text-sm font-medium
    focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111827]
    ${active ? 'text-white bg-[#1F2937]' : 'text-[#CBD5E1] bg-transparent hover:bg-[#1F2937] hover:text-white'}
  `.trim();

  return (
    <div className={`space-y-5 ${className || ''}`}>
      <div>
        <h3 className="text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors pl-2">
          Main
        </h3>
        <div className="mt-2 space-y-1">
          <Link
            to="/teacher/dashboard"
            className={menuItemClassName(currentPath === '/teacher/dashboard')}
          >
            <span className="whitespace-normal leading-tight">Dashboard</span>
          </Link>
        </div>
      </div>

      <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors px-2`}
          onClick={toggleMaster}
        >
          <h3>Master Management</h3>
          <IconComponent 
            icon={isMasterMenuOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${isMasterMenuOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${isMasterMenuOpen ? 'max-h-[500px]' : 'max-h-0'}`}>
          <Link
            to="/teacher/courses"
            className={menuItemClassName(isActiveLink('/courses'))}
          >
            <span className="whitespace-normal leading-tight">Courses</span>
          </Link>
          <Link
            to="/teacher/classes"
            className={menuItemClassName(isActiveLink('/classes'))}
          >
            <span className="whitespace-normal leading-tight">Classes</span>
          </Link>
          <Link
            to="/teacher/subjects"
            className={menuItemClassName(isActiveLink('/subjects'))}
          >
            <span className="whitespace-normal leading-tight">Subjects</span>
          </Link>
          <Link
            to="/teacher/chapters"
            className={menuItemClassName(isActiveLink('/chapters'))}
          >
            <span className="whitespace-normal leading-tight">Chapters</span>
          </Link>
          <Link
            to="/teacher/topics"
            className={menuItemClassName(isActiveLink('/topics'))}
          >
            <span className="whitespace-normal leading-tight">Topics</span>
          </Link>
          <Link
            to="/teacher/streams"
            className={menuItemClassName(isActiveLink('/streams'))}
          >
            <span className="whitespace-normal leading-tight">Streams</span>
          </Link>
          <Link
            to="/teacher/content"
            className={menuItemClassName(isActiveLink('/content'))}
          >
            <span className="whitespace-normal leading-tight">Content</span>
          </Link>
        </div>
      </div>

      <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors px-2`}
          onClick={toggleExam}
        >
          <h3>Exam Management</h3>
          <IconComponent 
            icon={isExamMenuOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${isExamMenuOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${isExamMenuOpen ? 'max-h-60' : 'max-h-0'}`}>
          <Link
            to="/teacher/questions"
            className={menuItemClassName(isActiveLink('/questions'))}
          >
            <span className="whitespace-normal leading-tight">Questions</span>
          </Link>
          <Link
            to="/teacher/exams"
            className={menuItemClassName(isActiveLink('/exams'))}
          >
            <span className="whitespace-normal leading-tight">Exams</span>
          </Link>
          <Link
            to="/teacher/offline-exam"
            className={menuItemClassName(isActiveLink('/offline-exam'))}
          >
            <span className="whitespace-normal leading-tight">Offline Exam</span>
          </Link>
        </div>
      </div>

    </div>
  );
};

export default TeacherSidebar;



